/**
 * The M1 asteroid field: a seeded layout shared by the renderer (G7) and the sim's static colliders
 * (statics.ts). Spec §8; the design is docs/art/sheets/asteroid-set.md, "Field composition (M1)".
 *
 * Generation runs once per preset change (never per frame), so it may allocate. It always builds the
 * full High list, then filters it, so Low ⊂ Medium ⊂ High with identical per-rock values: a size bin
 * keeps its first `count[preset]` rocks in generation order (a uniform random subset, since rocks are
 * drawn i.i.d.). Low is therefore exactly the rocks of 15 m and up.
 */
import type { Quat, Vec3 } from '../core/math'
import { createRng, nextFloat, range, type Rng } from '../core/rng'
import { FIELD_ARCHETYPES, type FieldConfig } from '../data/schemas'
import type { QualityPreset } from '../render/contracts'

/** Archetype indices (0-5 = A-F), as stored in `FieldLayout.archetype`. */
export const ARCHETYPE_COUNT = FIELD_ARCHETYPES.length
export const ARCHETYPE_E = FIELD_ARCHETYPES.indexOf('E')

/** Structure-of-arrays rock list. Index `i` is one rock in every array. */
export interface FieldLayout {
  readonly count: number
  /**
   * Stable rock id (M2, plan §2.4): the rock's index in the full High generation list, kept through the preset
   * filter, so the same rock has the same id on every preset. Strictly increasing with `i` (`fieldIndexOf`).
   */
  readonly id: Uint32Array
  /** The envelope centre (float64 world metres): the render instancing origin. */
  readonly origin: Readonly<Vec3>
  /** Rock centres, float64 world metres (D-011: rebase before handing to three.js or Rapier). */
  readonly x: Float64Array
  readonly y: Float64Array
  readonly z: Float64Array
  /** Mean radius (m). The ball collider is this × `colliderScale`. */
  readonly radius: Float32Array
  /** 0-5 = A-F. */
  readonly archetype: Uint8Array
  /** Per-rock shader seed in [0, 1). */
  readonly seed: Float32Array
  /** Unit spin axis, xyz interleaved (3 per rock). */
  readonly spinAxis: Float32Array
  /** Spin rate (rad/s); 0 for static rocks. */
  readonly spinRate: Float32Array
}

/** Float64 world pose of the derelict station. */
export interface DerelictPose {
  readonly position: Readonly<Vec3>
  readonly rotation: Readonly<Quat>
}

/** Safety cap on placement retries for one rock; hitting it means the config leaves no room. */
const MAX_ATTEMPTS = 100_000

const WORLD_ORIGIN: Readonly<Vec3> = { x: 0, y: 0, z: 0 }

/** True when (x, y, z) lies inside the field's flattened-ellipsoid envelope. */
export function isInsideFieldEnvelope(cfg: FieldConfig, x: number, y: number, z: number): boolean {
  const c = cfg.envelope.center
  const s = cfg.envelope.semiAxes
  const dx = (x - c[0]) / s[0]
  const dy = (y - c[1]) / s[1]
  const dz = (z - c[2]) / s[2]
  return dx * dx + dy * dy + dz * dz <= 1
}

function gaussian(rng: Rng): number {
  // Box-Muller; 1 - u keeps log away from 0.
  const u = 1 - nextFloat(rng)
  const v = nextFloat(rng)
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v)
}

function segmentDistanceSq(px: number, py: number, pz: number, a: readonly number[], b: readonly number[]): number {
  const abx = b[0] - a[0]
  const aby = b[1] - a[1]
  const abz = b[2] - a[2]
  const apx = px - a[0]
  const apy = py - a[1]
  const apz = pz - a[2]
  const len2 = abx * abx + aby * aby + abz * abz
  let t = len2 > 0 ? (apx * abx + apy * aby + apz * abz) / len2 : 0
  t = t < 0 ? 0 : t > 1 ? 1 : t
  const dx = apx - t * abx
  const dy = apy - t * aby
  const dz = apz - t * abz
  return dx * dx + dy * dy + dz * dz
}

/** True when the sphere's surface stays at least `clear` away from the point. */
function clearOfPoint(x: number, y: number, z: number, r: number, px: number, py: number, pz: number, clear: number): boolean {
  const dx = x - px
  const dy = y - py
  const dz = z - pz
  const min = clear + r
  return dx * dx + dy * dy + dz * dz >= min * min
}

/** Exact per-archetype counts for `n` rocks from percentages (largest remainder, ties to the lower index). */
function apportion(mix: readonly number[], n: number): number[] {
  const out = mix.map((m) => Math.floor((m / 100) * n))
  let left = n - out.reduce((a, v) => a + v, 0)
  const order = mix.map((m, i) => ({ i, rem: (m / 100) * n - out[i] })).sort((a, b) => b.rem - a.rem || a.i - b.i)
  for (let k = 0; left > 0; k = (k + 1) % order.length, left--) out[order[k].i]++
  return out
}

/**
 * Generates the seeded field for `preset`. `pocketCenter` is the spawn point (the sheet's world frame
 * puts it at the origin, as `world.json` spawn does). Throws if the constraints leave no room for a rock.
 */
export function generateField(
  cfg: FieldConfig,
  seed: number,
  preset: QualityPreset,
  pocketCenter: Readonly<Vec3> = WORLD_ORIGIN,
): FieldLayout {
  const rng = createRng(seed)
  const env = cfg.envelope
  const nClumps = cfg.clumps.length

  // Place the largest bins first, so small rocks fill in around the big ones.
  const binOrder = cfg.sizeBins.map((_, i) => i).sort((a, b) => cfg.sizeBins[b].radius[0] - cfg.sizeBins[a].radius[0])
  let total = 0
  for (const b of cfg.sizeBins) total += b.count.high

  const x = new Float64Array(total)
  const y = new Float64Array(total)
  const z = new Float64Array(total)
  const radius = new Float32Array(total)
  const archetype = new Uint8Array(total)
  const rockSeed = new Float32Array(total)
  const spinAxis = new Float32Array(total * 3)
  const spinRate = new Float32Array(total)
  const keep = new Uint8Array(total)

  // Per-clump archetype weight multipliers (the derelict clump: E × 3).
  const bias = cfg.clumps.map((c) => FIELD_ARCHETYPES.map((a) => (c.bias && c.bias.archetype === a ? c.bias.factor : 1)))
  const weights = new Float64Array(ARCHETYPE_COUNT)

  let n = 0
  for (const bi of binOrder) {
    const bin = cfg.sizeBins[bi]
    const keepCount = bin.count[preset]
    // The bin's exact archetype pool, drawn without replacement so the bin mix is exact on High.
    const pool = apportion(bin.mix, bin.count.high)
    const logMin = Math.log(bin.radius[0])
    const logMax = Math.log(bin.radius[1])

    for (let k = 0; k < bin.count.high; k++, n++) {
      // Log-uniform radius inside the bin (stored as float32; placement tests use the stored value).
      let r = Math.fround(Math.exp(range(rng, logMin, logMax)))
      if (r >= bin.radius[1]) r = Math.fround(bin.radius[1] - 1e-3)
      const fromClump = nextFloat(rng) < cfg.clumpShare
      const clump = fromClump ? Math.min(nClumps - 1, Math.floor(nextFloat(rng) * nClumps)) : -1

      let px = 0
      let py = 0
      let pz = 0
      let placed = false
      for (let attempt = 0; attempt < MAX_ATTEMPTS && !placed; attempt++) {
        if (clump >= 0) {
          const c = cfg.clumps[clump]
          px = c.center[0] + gaussian(rng) * c.sigma
          py = c.center[1] + gaussian(rng) * c.sigma * cfg.verticalSigmaScale
          pz = c.center[2] + gaussian(rng) * c.sigma
        } else {
          let ux: number
          let uy: number
          let uz: number
          do {
            ux = range(rng, -1, 1)
            uy = range(rng, -1, 1)
            uz = range(rng, -1, 1)
          } while (ux * ux + uy * uy + uz * uz > 1)
          px = env.center[0] + ux * env.semiAxes[0]
          py = env.center[1] + uy * env.semiAxes[1]
          pz = env.center[2] + uz * env.semiAxes[2]
        }
        placed = isPlaceable(cfg, pocketCenter, px, py, pz, r, x, y, z, radius, n)
      }
      if (!placed) throw new Error(`generateField: could not place rock ${n} (radius ${r.toFixed(1)} m) after ${MAX_ATTEMPTS} attempts`)

      // Archetype: weighted draw from what is left of the bin's pool, biased by the rock's clump.
      let sum = 0
      for (let a = 0; a < ARCHETYPE_COUNT; a++) {
        weights[a] = pool[a] * (clump >= 0 ? bias[clump][a] : 1)
        sum += weights[a]
      }
      let pick = nextFloat(rng) * sum
      let arch = -1
      for (let a = 0; a < ARCHETYPE_COUNT; a++) {
        if (weights[a] === 0) continue
        arch = a // the last non-empty archetype absorbs float round-off
        if (pick < weights[a]) break
        pick -= weights[a]
      }
      pool[arch]--

      // Spin: the first rule whose maxRadius exceeds the radius; larger rocks are static.
      let ax: number
      let ay: number
      let az: number
      let al: number
      do {
        ax = range(rng, -1, 1)
        ay = range(rng, -1, 1)
        az = range(rng, -1, 1)
        al = ax * ax + ay * ay + az * az
      } while (al > 1 || al < 1e-4)
      al = Math.sqrt(al)
      let rate = 0
      const spinPick = nextFloat(rng)
      for (let s = 0; s < cfg.spin.length; s++) {
        const rule = cfg.spin[s]
        if (r < rule.maxRadius) {
          rate = rule.radPerSec[0] + (rule.radPerSec[1] - rule.radPerSec[0]) * spinPick
          break
        }
      }

      x[n] = px
      y[n] = py
      z[n] = pz
      radius[n] = r
      archetype[n] = arch
      rockSeed[n] = nextFloat(rng)
      spinAxis[n * 3] = ax / al
      spinAxis[n * 3 + 1] = ay / al
      spinAxis[n * 3 + 2] = az / al
      spinRate[n] = rate
      keep[n] = k < keepCount ? 1 : 0
    }
  }

  const origin = { x: env.center[0], y: env.center[1], z: env.center[2] }
  if (preset === 'high') {
    const id = new Uint32Array(total)
    for (let i = 0; i < total; i++) id[i] = i
    return { count: total, origin, id, x, y, z, radius, archetype, seed: rockSeed, spinAxis, spinRate }
  }

  let count = 0
  for (let i = 0; i < total; i++) count += keep[i]
  const out = {
    count,
    origin,
    id: new Uint32Array(count),
    x: new Float64Array(count),
    y: new Float64Array(count),
    z: new Float64Array(count),
    radius: new Float32Array(count),
    archetype: new Uint8Array(count),
    seed: new Float32Array(count),
    spinAxis: new Float32Array(count * 3),
    spinRate: new Float32Array(count),
  }
  for (let i = 0, j = 0; i < total; i++) {
    if (!keep[i]) continue
    out.id[j] = i
    out.x[j] = x[i]
    out.y[j] = y[i]
    out.z[j] = z[i]
    out.radius[j] = radius[i]
    out.archetype[j] = archetype[i]
    out.seed[j] = rockSeed[i]
    out.spinAxis[j * 3] = spinAxis[i * 3]
    out.spinAxis[j * 3 + 1] = spinAxis[i * 3 + 1]
    out.spinAxis[j * 3 + 2] = spinAxis[i * 3 + 2]
    out.spinRate[j] = spinRate[i]
    j++
  }
  return out
}

/**
 * Index in `layout` of the rock with stable id `id`, or -1 when this preset does not have it. Binary search
 * over the increasing ids; allocation-free.
 */
export function fieldIndexOf(layout: Pick<FieldLayout, 'count' | 'id'>, id: number): number {
  let lo = 0
  let hi = layout.count - 1
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1
    const v = layout.id[mid]
    if (v === id) return mid
    if (v < id) lo = mid + 1
    else hi = mid - 1
  }
  return -1
}

/** Envelope, spawn pocket, lanes, derelict clearance and no overlap with the `n` rocks already placed. */
function isPlaceable(
  cfg: FieldConfig,
  pocket: Readonly<Vec3>,
  px: number,
  py: number,
  pz: number,
  r: number,
  x: Float64Array,
  y: Float64Array,
  z: Float64Array,
  radius: Float32Array,
  n: number,
): boolean {
  if (!isInsideFieldEnvelope(cfg, px, py, pz)) return false
  if (!clearOfPoint(px, py, pz, r, pocket.x, pocket.y, pocket.z, cfg.pocketRadius)) return false
  const d = cfg.derelict.position
  if (!clearOfPoint(px, py, pz, r, d[0], d[1], d[2], cfg.derelict.clearRadius)) return false
  for (let l = 0; l < cfg.lanes.length; l++) {
    const lane = cfg.lanes[l]
    const min = lane.clearRadius + r
    if (segmentDistanceSq(px, py, pz, lane.from, lane.to) < min * min) return false
  }
  for (let i = 0; i < n; i++) {
    const dx = px - x[i]
    const dy = py - y[i]
    const dz = pz - z[i]
    const min = r + radius[i]
    if (dx * dx + dy * dy + dz * dz < min * min) return false
  }
  return true
}

/**
 * The derelict's float64 world pose: station origin at `derelict.position`, its −Z face turned toward
 * `derelict.faceToward` (the spawn), then rolled `derelict.roll` about that facing axis so the spindle
 * (+Y) leans off the system plane.
 */
export function derelictPose(cfg: FieldConfig): DerelictPose {
  const [px, py, pz] = cfg.derelict.position
  const [tx, ty, tz] = cfg.derelict.faceToward
  // Local +Z points away from the target.
  let zx = px - tx
  let zy = py - ty
  let zz = pz - tz
  const zl = Math.hypot(zx, zy, zz)
  if (zl === 0) throw new Error('field.json derelict: faceToward equals position')
  zx /= zl
  zy /= zl
  zz /= zl
  // Level frame: x = up × z (world +Y, or +Z when facing straight up or down), y = z × x.
  const vertical = Math.abs(zy) > 0.999999
  const upy = vertical ? 0 : 1
  const upz = vertical ? 1 : 0
  let xx = upy * zz - upz * zy
  let xy = upz * zx
  let xz = -upy * zx
  const xl = Math.hypot(xx, xy, xz)
  xx /= xl
  xy /= xl
  xz /= xl
  let yx = zy * xz - zz * xy
  let yy = zz * xx - zx * xz
  let yz = zx * xy - zy * xx
  // Roll about local Z: x' = x cos + y sin, y' = y cos − x sin.
  const c = Math.cos(cfg.derelict.roll)
  const s = Math.sin(cfg.derelict.roll)
  const rx = xx * c + yx * s
  const ry = xy * c + yy * s
  const rz = xz * c + yz * s
  yx = yx * c - xx * s
  yy = yy * c - xy * s
  yz = yz * c - xz * s
  xx = rx
  xy = ry
  xz = rz
  return { position: { x: px, y: py, z: pz }, rotation: quatFromBasis(xx, xy, xz, yx, yy, yz, zx, zy, zz) }
}

/** Quaternion of the rotation matrix whose columns are the local x, y and z axes in world space. */
function quatFromBasis(
  m00: number,
  m10: number,
  m20: number,
  m01: number,
  m11: number,
  m21: number,
  m02: number,
  m12: number,
  m22: number,
): Quat {
  const trace = m00 + m11 + m22
  let x: number
  let y: number
  let z: number
  let w: number
  if (trace > 0) {
    const s = 0.5 / Math.sqrt(trace + 1)
    w = 0.25 / s
    x = (m21 - m12) * s
    y = (m02 - m20) * s
    z = (m10 - m01) * s
  } else if (m00 > m11 && m00 > m22) {
    const s = 2 * Math.sqrt(1 + m00 - m11 - m22)
    w = (m21 - m12) / s
    x = 0.25 * s
    y = (m01 + m10) / s
    z = (m02 + m20) / s
  } else if (m11 > m22) {
    const s = 2 * Math.sqrt(1 + m11 - m00 - m22)
    w = (m02 - m20) / s
    x = (m01 + m10) / s
    y = 0.25 * s
    z = (m12 + m21) / s
  } else {
    const s = 2 * Math.sqrt(1 + m22 - m00 - m11)
    w = (m10 - m01) / s
    x = (m02 + m20) / s
    y = (m12 + m21) / s
    z = 0.25 * s
  }
  const l = Math.hypot(x, y, z, w)
  return { x: x / l, y: y / l, z: z / l, w: w / l }
}
